import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import playIcon from "../../assets/icons/play_icon.png";
import pauseIcon from "../../assets/icons/pause_icon.png";

/**
 * Reusable video player with hover pause/play overlay
 * Props:
 * - src (string): Video file path
 * - width, height (object|string|number): responsive or fixed
 * - autoPlay (boolean): optional autoplay
 * - muted (boolean): optional mute
 * - iconSize (object|number): responsive icon size
 * - sx (object): optional custom MUI sx styling
 */
const VideoPlayer = ({
  src,
  width = "100%",
  height = { xs: 200, sm: 280 },
  autoPlay = false,
  muted = false,
  iconSize = { xs: 60, sm: 75, md: 85 },
  sx = {},
  zIndex = "1",
  borderRadius = "10px",
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef(null);

  // Setup video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedData = () => {
      video.currentTime = 0; // show first frame
    };

    const handleEnded = () => {
      video.currentTime = 0;
      setIsPlaying(false);
    };

    video.addEventListener("loadeddata", handleLoadedData);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("loadeddata", handleLoadedData);
      video.removeEventListener("ended", handleEnded);
    };
  }, []);

  // Toggle play/pause
  const handleVideoClick = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      video.currentTime = 0;
      setIsPlaying(false);
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 4,
        overflow: "hidden",
        cursor: "pointer",
        width,
        height,
        zIndex,
        borderRadius,
        ...sx,
      }}
      onClick={handleVideoClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Video */}
      <Box
        component="video"
        ref={videoRef}
        src={src}
        playsInline
        muted={muted}
        controls={false}
        preload="metadata"
        autoPlay={autoPlay}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius: 4,
          display: "block",
        }}
      />

      {/* Play button (paused) */}
      {!isPlaying && (
        <Box
          component="img"
          src={playIcon}
          alt="Play"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 2,
            pointerEvents: "none",
            width: iconSize,
            height: iconSize,
            filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.4))",
            transition: "transform 0.3s ease",
          }}
        />
      )}

      {/* Pause button (hover while playing) */}
      {isPlaying && isHovered && (
        <Box
          component="img"
          src={pauseIcon}
          alt="Pause"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) scale(1.1)",
            zIndex: 2,
            pointerEvents: "none",
            width: iconSize,
            height: iconSize,
            filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.4))",
            transition: "transform 0.3s ease, opacity 0.3s ease",
          }}
        />
      )}
    </Box>
  );
};

export default VideoPlayer;
